// Horizontal event-timeline stepper for a delivery record — one node per
// lifecycle event ([2.1]'s DeliveryEvent history), connected by a divider line,
// each carrying an icon tile, a status pill, and a formatted timestamp. Pure CSS
// layout (flex row + border-t dividers), no charting library. Scrolls
// horizontally on narrow viewports rather than breaking the page.
import {
	AlertCircle,
	AlertTriangle,
	Check,
	Clock,
	Mail,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import type { DeliveryEvent, DeliveryEventType } from '@/api/delivery-log/mock';
import { Badge, type BadgeStatus } from '@/components/ui/badge';

/** Event type → status-pill color (distinct enum from DeliveryStatus). */
const EVENT_TO_BADGE: Record<DeliveryEventType, BadgeStatus> = {
	sent: 'info',
	delivered: 'success',
	bounced: 'warning',
	failed: 'danger',
	pending: 'neutral',
};

/** Event type → lucide icon rendered inside the node tile. */
const EVENT_TO_ICON: Record<DeliveryEventType, LucideIcon> = {
	sent: Mail,
	delivered: Check,
	bounced: AlertTriangle,
	failed: AlertCircle,
	pending: Clock,
};

/** Event type → icon-tile tint (mirrors the pill color family). */
const EVENT_TO_TILE: Record<DeliveryEventType, string> = {
	sent: 'border-info/40 bg-info/10 text-info',
	delivered: 'border-success/40 bg-success/10 text-success',
	bounced: 'border-warning/40 bg-warning/10 text-warning',
	failed: 'border-danger/40 bg-danger/10 text-danger',
	pending: 'border-border bg-muted text-muted-foreground',
};

/** Human-readable node label per event type. */
const EVENT_LABEL: Record<DeliveryEventType, string> = {
	sent: 'Sent',
	delivered: 'Delivered',
	bounced: 'Bounced',
	failed: 'Failed',
	pending: 'Retrying',
};

function formatEventTimestamp(iso: string): string {
	return new Date(iso).toLocaleString(undefined, {
		month: 'short',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
	});
}

export interface EventTimelineProps {
	events: DeliveryEvent[];
}

export function EventTimeline({ events }: EventTimelineProps) {
	if (events.length === 0) return null;

	return (
		<section aria-label="Delivery event timeline">
			<h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
				Timeline
			</h2>
			<ol className="mt-3 flex items-start overflow-x-auto pb-1">
				{events.map((event, i) => {
					const Icon = EVENT_TO_ICON[event.type];
					const isLast = i === events.length - 1;

					return (
						<li
							key={`${event.type}-${event.timestamp}-${i}`}
							className="flex min-w-0 shrink-0 items-start"
						>
							<div className="flex w-28 flex-col items-center gap-2 text-center">
								<div
									className={`flex size-10 items-center justify-center rounded-md border [&_svg]:size-5 ${EVENT_TO_TILE[event.type]}`}
								>
									<Icon aria-hidden="true" />
								</div>
								<Badge status={EVENT_TO_BADGE[event.type]}>
									{EVENT_LABEL[event.type]}
								</Badge>
								<span
									suppressHydrationWarning
									className="text-xs tabular-nums text-muted-foreground"
								>
									{formatEventTimestamp(event.timestamp)}
								</span>
							</div>
							{!isLast ? (
								<div
									aria-hidden="true"
									className="mt-5 w-8 shrink-0 border-t border-border-strong sm:w-12"
								/>
							) : null}
						</li>
					);
				})}
			</ol>
		</section>
	);
}
